import React, { useState, useEffect, useRef, useMemo } from 'react'
import { PersonalEmailWarning } from './PersonalEmailWarning'
import { Box } from '@material-ui/core'
import { debounce } from 'lodash'
import { PATHS } from 'Config/Routing/paths'
import Link from '../../../../../../Components/Link'
import Input from '../../../../../../Components/Inputs/Input'
import InputPassword from '../../../../../../Components/Inputs/InputPassword'
import SubmitButton from '../../../../../../Components/Form/SubmitButton'
import { checkIfUserExist } from '../../../graphQL'
import { isProduction } from '../../../../../../Utils/Functions'
import { STEPS } from '../../../options'
import Captcha from '../../Captcha'
import { useWrappedPosthog } from '../../../../../../Components/PostHogProvider/hooks'
import { SocialAuthButton } from '../../../../components/SocialAuthButton/SocialAuthButton'
import { OrSeparate } from '../../../../components/OrSeparate/OrSeparate'
import { AUTH_METHOD } from '../../../../../../constants'
import {
  getEmailFieldLabel,
  isPersonalEmail,
} from '../../../../../../Utils/EmailValidation'

export const Step1 = ({
  title,
  fields,
  onChange,
  setStep,
  authMethod,
  isShow,
  isEDDMUser,
  userCountry,
  shouldRestrictToEddmOnly,
  setShouldRestrictToEddmOnly,
  toolsParams,
}) => {
  const posthog = useWrappedPosthog()

  const [isChecking, setIsChecking] = useState(false)
  const [isCaptchaSuccess, setIsCaptchaSuccess] = useState(
    isProduction() ? false : true,
  )
  const [disabled, setDisabled] = useState(true)

  const lastCheckedEmailRef = useRef('')

  const onCheckExistUser = useMemo(
    () =>
      debounce(async email => {
        if (email !== lastCheckedEmailRef.current) return

        if (!fields.email.error) {
          const response = await checkIfUserExist(email)
          const {
            data: { isExistUser },
          } = await response.json()

          if (email === lastCheckedEmailRef.current) {
            onChange('email')(
              null,
              isExistUser ? true : false,
              isExistUser
                ? 'An account with this email already exists.'
                : false,
            )
          }
        }
        setIsChecking(false)
      }, 500),
    [fields.email, onChange],
  )

  useEffect(() => {
    onCheckExistUser.cancel()

    if (!fields.email.value) {
      setIsChecking(false)
      return
    }

    lastCheckedEmailRef.current = fields.email.value
    setIsChecking(true)
    onCheckExistUser(fields.email.value)
  }, [fields.email.value])

  const onSubmit = async e => {
    e.preventDefault()
    if (posthog) {
      posthog.capture('Clicked on Continue with Email on the Sign up', {
        'event_category': 'Signup',
      })
    }
    setStep(STEPS.STEP_2)
  }

  const onChangeCaptcha = value => {
    if (isProduction()) {
      setIsCaptchaSuccess(!!value)
    }
  }

  useEffect(() => {
    let stepFields = Object.keys(fields).reduce((acc, key) => {
      if (authMethod === AUTH_METHOD.OPPIZI) {
        if (['email', 'password'].includes(key)) {
          acc[key] = fields[key]
        }
      } else {
        if (['email'].includes(key)) {
          acc[key] = fields[key]
        }
      }
      return acc
    }, {})

    const isFieldsHasError = Object.values(stepFields).some(
      ({ error, additionalError }) => error || additionalError,
    )

    if (isFieldsHasError || isChecking) {
      setDisabled(true)
    } else {
      if (isCaptchaSuccess) {
        setDisabled(false)
      } else {
        setDisabled(true)
      }
    }
  }, [fields, isCaptchaSuccess, isChecking, authMethod])

  useEffect(() => {
    if (isEDDMUser) return
    if (shouldRestrictToEddmOnly) {
      onChange('countryId')('14', false)
    } else {
      onChange('countryId')('', true)
    }
  }, [shouldRestrictToEddmOnly, isEDDMUser])

  const onEmailChange = (value, error) => {
    if (error || isEDDMUser || !userCountry) {
      onChange('email')(value, error)
      if (shouldRestrictToEddmOnly) {
        setShouldRestrictToEddmOnly(false)
      }
    } else if (userCountry.countryCode !== 'US') {
      const hasError = isPersonalEmail(value)
      onChange('email')(
        value,
        hasError,
        hasError ? 'Please enter a work email' : '',
      )
    } else {
      if (value) {
        const isPersonal = isPersonalEmail(value)
        setShouldRestrictToEddmOnly(isPersonal)
      } else {
        setShouldRestrictToEddmOnly(false)
      }
      onChange('email')(value, false)
    }
  }

  return (
    <div style={{ display: isShow ? 'block' : 'none' }}>
      <h1 style={{ textAlign: 'left' }}>{title}</h1>
      <Box sx={{ marginBottom: 32 }}>
        <p style={{ textAlign: 'left', fontWeight: 'bold' }}>
          No credit card required
        </p>
      </Box>
      <div className="buttonsContainer">
        <SocialAuthButton type="google" />
      </div>
      <OrSeparate />
      <Input
        label={getEmailFieldLabel(isEDDMUser)}
        value={fields.email.value}
        additionalError={fields.email.additionalError}
        onChange={onEmailChange}
        required
        email
        cypress_id={'emailInput'}
      />
      {shouldRestrictToEddmOnly && authMethod === AUTH_METHOD.OPPIZI && (
        <PersonalEmailWarning />
      )}
      <InputPassword
        value={fields.password.value}
        onChange={onChange('password')}
        authMethod={authMethod}
        className="inputMargin"
        cypress_id={'passwordInput'}
      />
      <Captcha onChangeCaptcha={onChangeCaptcha} />
      <SubmitButton
        className={'signUpButton'}
        type="submit"
        disabled={disabled}
        onClick={onSubmit}
      >
        Get Started Now
      </SubmitButton>
      <div className={'link'}>
        Already have an account?{' '}
        <Link
          text="Login"
          to={`${PATHS.main.login}${
            toolsParams.status && toolsParams.credentialId
              ? `?status=${toolsParams.status}&credentialId=${
                  toolsParams.credentialId
                }&shopName=${toolsParams.shopName}&shopDomain=${
                  toolsParams.shopDomain
                }`
              : ''
          }`}
        />
        .
      </div>
      <div className={'link'}>
        Want to work with us? Sign in as a{' '}
        <a href="https://www.oppizi.com/brand-ambassadors" className={'links'}>
          Brand Ambassador
        </a>
        .
      </div>
    </div>
  )
}
